// Every cost in the game, on one page, mid-game.
//
// The rules document explains the game; this answers the question a player
// actually has with a trade on the board — what does a knight cost, what does
// it leave, what will this blow do. It follows `rulesComponents.tsx`'s one
// discipline: **anything with a number in it asks the rules**, so the page
// cannot quietly drift out of step the way a hand-written list of costs
// always eventually does. The prose between the numbers is the only thing
// authored here.

import {
  Btn,
  Modal,
  ModalBody,
  ModalTitle,
  Notice,
  Pill,
  SectionTitle,
  styled,
  Table,
  TableScroll,
  Text
} from "./kit";

import {
  BISHOP,
  KING,
  KNIGHT,
  PAWN,
  type PieceType,
  QUEEN,
  ROOK
} from "src/engine/rules/board";
import {
  MINTABLE,
  CAPTURE_DAMAGE,
  PIECE_HEALTH,
  PIECE_NAMES,
  PIECE_VALUE,
  RULES,
  SURRENDER_VALUE
} from "src/engine/rules/rules";
import {
  FUEL,
  isLadder,
  LADDERS,
  METAL,
  type MaterialLine,
  materialName,
  materialValue,
  mintBill,
  STONE
} from "src/engine/rules/materials";
import { PALETTE } from "src/client/ai/render/palette";
import { PieceMark } from "./PieceMark";

/**
 * Which game the numbers are for.
 *
 * **The one surface in the chrome that still reads the rules directly**
 * (PLAN-CLIENT-SPLIT day 8): every number here asks `RULES`, the ladders and
 * the bills, and none of that is on the contract yet. What it wants is a
 * `game.board.costs()` — the ranks with their bill, blow, health and scrap,
 * the ladders with each rung's worth and tone, the climb, and the handful of
 * `RULES` the prose quotes — said in `PriceRow`s and names.
 */
export interface CostsDialogProps {
  open: boolean;
  onClose: () => void;
}

/** The ranks the table lists, in the order a chess player expects. */
const RANKS: readonly PieceType[] = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN, KING];

/**
 * What each rank does for the factory, in one line. The one hand-authored
 * column — everything numeric in it still reads `RULES`.
 */
const ROLE: Record<number, string> = {
  [PAWN]: `Digs ${RULES.minerRate} a turn from the heap it stands on. Moves and strikes the way it faces.`,
  [KNIGHT]: `Courier — holds ${RULES.carrierBuffer}. Can spend its move turning a pawn instead.`,
  [BISHOP]: `Oven — cooks one unit a grade higher each turn.`,
  [ROOK]: `Forge — holds ${RULES.foundryBuffer}, mints new pieces, aims the pawns it mints.`,
  [QUEEN]: `Sweeps up everything on every square she crosses; holds ${RULES.queenBuffer}.`,
  [KING]: `Oven and forge in one — one job a turn, faster every turn it keeps it (up to ${RULES.kingTempoMax}×). Lifts whatever it lands on, whole.`
};

/**
 * A grade's colour per line, matching the ingots on the board — the same rule
 * `rulesComponents.gradeColor` and the shelf's `UNIT_TONE` follow: a material
 * has to be one colour everywhere or the ladder stops being legible.
 *
 * **The one place a hex is allowed here**, because it is not a theme colour: a
 * material's colour is the board's own and comes off the renderer's palette
 * (the rule `unitRows.tsx` states). `undefined` means "no such rung", which the
 * call site draws in the ink it inherits rather than in an invented tone.
 */
function lineTone(line: number, grade: number): string | undefined {
  const table =
    line === METAL
      ? PALETTE.grades
      : line === STONE
        ? PALETTE.stoneGrades
        : line === FUEL
          ? PALETTE.fuelGrades
          : PALETTE.specialGrades;
  const value = table[grade] ?? table[0];
  return value === undefined ? undefined : `#${value.toString(16).padStart(6, "0")}`;
}

/** One bill, as words: "6 Steel", "18 Crucible Steel + 1 Electrum". */
function billLabel(type: PieceType): string {
  return mintBill(type)
    .map((row) => `${row.count} ${materialName(row.line, row.grade)}`)
    .join(" + ");
}

/**
 * A group's heading, ruled under.
 *
 * **The table has to fit the pause plate**, which is the width this page was
 * owed since U3: the body is `min(1120px, 94vw)` less a 232px nav and 52px of
 * padding — about 830px at the widest and rather less on a laptop. So no
 * column is given a minimum, the role sentence wraps, and only the two numeric
 * columns refuse to; `TableScroll` catches whatever is left.
 */
const Head = styled("div")(({ theme }) => ({
  marginTop: theme.kit.space.lg,
  marginBottom: theme.kit.space.xs,
  paddingBottom: theme.kit.space.xs,
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

/** The costs table's own column rules, over the kit's. */
const Costs = styled(Table)({
  "& th.num, & td.num": { textAlign: "right", whiteSpace: "nowrap" },
  // The role sentence is what makes this column wide, so it is the one that
  // gives: everything else is short enough to sit on one line by itself.
  "& td.role": { minWidth: 0 }
});

/** A list of the facts that are not numbers in a column. */
const Notes = styled("ul")(({ theme }) => ({
  margin: `${theme.kit.space.sm}px 0 0`,
  paddingLeft: theme.kit.space.lg,
  color: theme.kit.color.inkDim,
  fontSize: 12.5,
  lineHeight: 1.6,
  "& li": { marginBottom: theme.kit.space.xs },
  "& em": { color: theme.kit.color.ink }
}));

/** The material ladders and the climb, as rows of pills. */
const Ladder = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.kit.space.sm,
  marginBottom: theme.kit.space.sm
}));

/** One rung and the arrow into it, so a wrap never orphans the arrow. */
const Step = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.sm
}));

const Arrow = styled("span")(({ theme }) => ({ color: theme.kit.color.inkDim }));

/** The title and its close, on one line — the frame's only bespoke geometry. */
const TitleRow = styled(ModalTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.kit.space.md
}));

/** The dagger under the table, and the callout under that. */
const Footnote = styled("p")(({ theme }) => ({
  margin: `${theme.kit.space.xs}px 0 0`,
  lineHeight: 1.5
}));

const BlowNotice = styled(Notice)(({ theme }) => ({
  marginTop: theme.kit.space.md,
  "& strong": { color: theme.kit.color.cream }
}));

const Line = styled(Text)(({ theme }) => ({
  display: "block",
  "& + &": { marginTop: theme.kit.space.sm }
}));

/**
 * The tables themselves, with no frame round them.
 *
 * Split out so the pause menu's Costs screen shows the same page inside the
 * plate (PLAN-UI §28.3) rather than opening a second dialog over the first —
 * and so there is one copy of the numbers, which is the discipline this whole
 * file exists under.
 */
export function CostsContent() {
  return (
    <>
      {/* ------------------------------------------------ the six ranks */}
      <Head>
        <SectionTitle>Pieces</SectionTitle>
      </Head>
      <TableScroll>
        <Costs>
          <thead>
            <tr>
              <th>Piece</th>
              <th>Forge cost</th>
              <th className="num">Blow</th>
              <th className="num">Health</th>
              <th>Leaves</th>
            </tr>
          </thead>
          <tbody>
            {RANKS.map((type) => {
              const mintable = MINTABLE.includes(type);
              const bill = mintable ? mintBill(type) : [];
              return (
                <tr key={type} data-testid={`costs-piece-${type}`}>
                  <td className="role">
                    <Text block weight={700}>
                      <PieceMark type={type} size={16} inline /> {PIECE_NAMES[type]}
                    </Text>
                    <Text block tone="dim" size={12}>
                      {ROLE[type]}
                    </Text>
                  </td>
                  <td>
                    {mintable ? (
                      <Text
                        weight={700}
                        style={{ color: lineTone(bill[0].line, bill[0].grade) }}
                      >
                        {billLabel(type)}
                      </Text>
                    ) : (
                      <Text tone="dim">not forgeable</Text>
                    )}
                  </td>
                  {/* Blow is the brand and health the cream — the two the rest
                      of the interface already means by "this acts" and "this
                      endures". Neither is a material colour. */}
                  <td className="num">
                    <Text role="display" tone="brand" weight={700}>
                      {CAPTURE_DAMAGE[type]}
                    </Text>
                  </td>
                  <td className="num">
                    <Text role="display" tone="cream" weight={700}>
                      {PIECE_HEALTH[type]}
                      {type === KING ? "†" : ""}
                    </Text>
                  </td>
                  <td>
                    <Text>
                      {type === KING
                        ? `${SURRENDER_VALUE[KING]} on surrender`
                        : `${PIECE_VALUE[type]} scrap`}
                    </Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Costs>
      </TableScroll>
      <Footnote>
        <Text tone="dim" size={12}>
          † A <em>last</em> king falls to any blow — check and mate mean exactly
          what they mean in chess. A spare king takes the table&apos;s row.
        </Text>
      </Footnote>

      <BlowNotice tone="warn">
        <Line size={13}>
          <strong>How a blow lands:</strong> at or above the target&apos;s
          remaining health, it takes the piece — exactly as chess always did.
          Short of that, the target keeps standing, loses that much health, and
          is shoved one square along the blow. The board rings a killing blow{" "}
          <Text tone="rust" weight={700}>
            red
          </Text>{" "}
          and a shove{" "}
          <Text tone="brand" weight={700}>
            amber
          </Text>
          .
        </Line>
        <Line size={13}>
          <strong>Buildings take blows too:</strong> every one stands at{" "}
          {RULES.structureHealth} health, which is more than the hardest blow in
          the game — so nothing knocks a warehouse down in a single move.
          Masonry is never shoved: your attacker stops one square short and
          swings again next turn.
        </Line>
      </BlowNotice>

      {/* ------------------------------------------------ the material */}
      <Head>
        <SectionTitle>Materials</SectionTitle>
      </Head>
      {LADDERS.map((ladder, line) =>
        ladder.length === 0 ? null : (
          <Ladder key={line}>
            {ladder.map((name, grade) => {
              const tone = lineTone(line, grade);
              return (
                <Step key={name}>
                  {grade > 0 && <Arrow>{isLadder(line) ? "→" : "|"}</Arrow>}
                  <Pill style={tone ? { borderColor: tone, color: tone } : undefined}>
                    {name}
                    <Text role="display" tone="dim" size={11}>
                      worth {materialValue(line as MaterialLine, grade)}
                    </Text>
                  </Pill>
                </Step>
              );
            })}
          </Ladder>
        )
      )}
      <Notes>
        <li>
          Refining lifts a unit one grade a turn and never changes the count —
          material gets <em>better</em>, never more.
        </li>
        <li>
          No machine cooks the same unit twice, so each rung of the ladder is
          one more <em>different</em> oven the unit has passed through.
        </li>
        <li>
          Stone refines free. Raising metal a rung costs a fuel point, and a
          refiner makes its own by burning a unit of coal — which takes its
          whole tick, so metal climbs at half stone&apos;s pace. Points left
          unspent are gone by your next turn.
        </li>
        <li>
          A belt part costs {RULES.beltCost}{" "}
          {materialName(STONE, RULES.beltGrade)}, paid at once from a piece
          beside it or your nearest store — and comes back on the ground when
          the part is lifted or broken.
        </li>
        <li>
          A minted piece costs more than its wreck returns. The forge is the
          one place material leaves the world — everywhere else it only moves.
          Material refined past what a bill asks for still pays it, the lowest
          rung that qualifies first.
        </li>
      </Notes>

      {/* ------------------------------------------------ taking apart */}
      <Head>
        <SectionTitle>Taking apart</SectionTitle>
      </Head>
      <Notes>
        <li>
          Sacrifice takes a piece apart for exactly what its wreck is worth, at
          its forge grade — always half of what it cost to make. The board keeps
          the metal; you paid for the convenience.
        </li>
      </Notes>

      {/* ------------------------------------------------ tempo facts */}
      <Head>
        <SectionTitle>Tempo</SectionTitle>
      </Head>
      <Notes>
        <li>
          A turn is one move <em>plus</em> as much construction as you can pay
          for. Building costs ground and iron, never tempo.
        </li>
        <li>Cargo moves one square a turn, one unit per part.</li>
        <li>
          Kings and queens pass material on at {RULES.royalHandoffRate} units a
          turn; everything else hands over one.
        </li>
        <li>
          Everybody&apos;s factory ticks on <em>every</em> move played, not only
          on its owner&apos;s.
        </li>
      </Notes>
    </>
  );
}

/**
 * The dialog frame. Kept because the reference is still openable as a dialog
 * from anywhere that has no plate to put it in; the pause menu renders
 * `CostsContent` directly.
 */
export function CostsDialog({ open, onClose }: CostsDialogProps) {
  return (
    <Modal open={open} onClose={onClose} fullWidth maxWidth="md" data-testid="costs-dialog">
      <TitleRow>
        Costs &amp; numbers
        <Btn
          variant="ghost"
          small
          aria-label="Close"
          data-testid="costs-close"
          onClick={onClose}
        >
          ✕
        </Btn>
      </TitleRow>
      <ModalBody>
        <CostsContent />
      </ModalBody>
    </Modal>
  );
}
